/**
 * Admin API - Users, organizations, sites, monitoring, onboarding
 * @module api/admin
 */

import { API_BASE_URL, apiRequest, getAuthHeaders, uiApiRequest } from './core.js';
import { vehicleUsagesAPI } from './vehicles.js';

// =============================================================================
// USER API
// =============================================================================

/**
 * User API - User management
 */
export const userAPI = {
  getAll: () => apiRequest('/user'),
  getById: (id) => apiRequest(`/user/${id}`),
  create: (user) => apiRequest('/user', {
    method: 'POST',
    body: JSON.stringify(user),
  }),
  update: (id, user) => apiRequest(`/user/${id}`, {
    method: 'PUT',
    body: JSON.stringify(user),
  }),
  delete: (id) => apiRequest(`/user/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// USER ORG ACCESS API — multi-org access grants (chantier Groupe multi-filiales)
// =============================================================================

/**
 * User Org Access API - grant/revoke a user's access to organizations of the
 * current tenant beyond their primary_org_id. Backed by the dedicated,
 * admin-gated router at /user-org-access (NOT the generic /user_org_access
 * entity CRUD — see backend/routes/user_org_access_routes.mjs).
 */
export const userOrgAccessAPI = {
  /** List grants, optionally filtered to one user. Admin/org_admin only (403 otherwise). */
  list: (userId) => apiRequest(`/user-org-access${userId ? `?user_id=${userId}` : ''}`),

  /** Grant `orgId` (code or numeric id) to `userId`. org_id must belong to the caller's tenant (404 otherwise). */
  grant: (userId, orgId, role) => apiRequest('/user-org-access', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, org_id: orgId, role }),
  }),

  /** Revoke a grant by its id. 404 if it does not belong to the caller's tenant. */
  revoke: (id) => apiRequest(`/user-org-access/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// ORGANIZATION API
// =============================================================================

/**
 * Organization API - Multi-org management
 */
export const organizationAPI = {
  getAll: () => apiRequest('/organization'),
  getById: (id) => apiRequest(`/organization/${id}`),
  create: (organization) => apiRequest('/organization', {
    method: 'POST',
    body: JSON.stringify(organization),
  }),
  update: (id, organization) => apiRequest(`/organization/${id}`, {
    method: 'PUT',
    body: JSON.stringify(organization),
  }),
  delete: (id) => apiRequest(`/organization/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// WORKING SITE API
// =============================================================================

/**
 * Working Site API - Physical locations
 */
export const workingSiteAPI = {
  getAll: () => apiRequest('/working_site?_limit=5000'),
  getById: (id) => apiRequest(`/working_site/${id}`),
  getByOrg: (orgId) => apiRequest(`/working_site?org_id=${orgId}`),
  create: (site) => apiRequest('/working_site', {
    method: 'POST',
    body: JSON.stringify(site),
  }),
  update: (id, site) => apiRequest(`/working_site/${id}`, {
    method: 'PUT',
    body: JSON.stringify(site),
  }),
  delete: (id) => apiRequest(`/working_site/${id}`, {
    method: 'DELETE',
  }),
  // Operational site statistics (uses middleware /sites endpoint)
  getStats: () => apiRequest('/sites/stats'),
  getDetails: (id) => apiRequest(`/sites/${id}/details`),
  // Planning du site (réservations confirmées à venir) : une seule définition
  // de l'appel, dans `vehicleUsagesAPI` — on l'expose ici sous le nom du site.
  getPlanning: (id, range) => vehicleUsagesAPI.getSitePlanning(id, range),
  updateGps: (id, lat, lng) => apiRequest(`/sites/${id}/gps`, {
    method: 'PUT',
    body: JSON.stringify({ gps_latitude: lat, gps_longitude: lng }),
  }),
};

// =============================================================================
// UI PROFILE API
// =============================================================================

/**
 * UI Profile API - User interface profiles
 */
export const uiProfileAPI = {
  getAll: () => apiRequest('/ui_profile'),
  getById: (id) => apiRequest(`/ui_profile/${id}`),
  getByCode: (code) => apiRequest(`/ui_profile?code=${code}`),
  getDefault: () => apiRequest('/ui_profile?is_default=1'),
  getActive: () => apiRequest('/ui_profile?status=active'),

  create: (profile) => apiRequest('/ui_profile', {
    method: 'POST',
    body: JSON.stringify({
      ...profile,
      modules: typeof profile.modules === 'string' ? profile.modules : JSON.stringify(profile.modules),
      widgets: typeof profile.widgets === 'string' ? profile.widgets : JSON.stringify(profile.widgets),
      tab_overrides: typeof profile.tab_overrides === 'string' ? profile.tab_overrides : JSON.stringify(profile.tab_overrides || {}),
      quick_actions: typeof profile.quick_actions === 'string' ? profile.quick_actions : JSON.stringify(profile.quick_actions || []),
    }),
  }),

  update: (id, profile) => apiRequest(`/ui_profile/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      ...profile,
      modules: typeof profile.modules === 'string' ? profile.modules : JSON.stringify(profile.modules),
      widgets: typeof profile.widgets === 'string' ? profile.widgets : JSON.stringify(profile.widgets),
      tab_overrides: typeof profile.tab_overrides === 'string' ? profile.tab_overrides : JSON.stringify(profile.tab_overrides || {}),
      quick_actions: typeof profile.quick_actions === 'string' ? profile.quick_actions : JSON.stringify(profile.quick_actions || []),
    }),
  }),

  delete: (id) => apiRequest(`/ui_profile/${id}`, {
    method: 'DELETE',
  }),

  duplicate: async (id, newCode, newName) => {
    const original = await apiRequest(`/ui_profile/${id}`);
    if (!original.ok) throw new Error('Profile not found');
    const { id: _, created_at, updated_at, is_system, ...profileData } = original.data;
    return apiRequest('/ui_profile', {
      method: 'POST',
      body: JSON.stringify({
        ...profileData,
        code: newCode,
        name: newName,
        is_system: 0,
        is_default: 0,
        modules: typeof profileData.modules === 'string' ? profileData.modules : JSON.stringify(profileData.modules || []),
        widgets: typeof profileData.widgets === 'string' ? profileData.widgets : JSON.stringify(profileData.widgets || []),
        tab_overrides: typeof profileData.tab_overrides === 'string' ? profileData.tab_overrides : JSON.stringify(profileData.tab_overrides || {}),
        quick_actions: typeof profileData.quick_actions === 'string' ? profileData.quick_actions : JSON.stringify(profileData.quick_actions || []),
      }),
    });
  },

  setDefault: async (id) => {
    const profiles = await apiRequest('/ui_profile?is_default=1');
    if (profiles.ok && profiles.data.length > 0) {
      for (const p of profiles.data) {
        await apiRequest(`/ui_profile/${p.id}`, {
          method: 'PUT',
          body: JSON.stringify({ is_default: 0 }),
        });
      }
    }
    return apiRequest(`/ui_profile/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_default: 1 }),
    });
  },

  assignToUser: (userId, profileId) => apiRequest(`/user/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ ui_profile_id: profileId }),
  }),

  getUsersByProfile: (profileId) => apiRequest(`/user?ui_profile_id=${profileId}`),
};

// =============================================================================
// WIDGET REGISTRY API - Database-backed widget catalog
// =============================================================================

export const widgetRegistryAPI = {
  getAll: () => apiRequest('/widget_registry?_sort=display_order&_order=asc'),
  getActive: () => apiRequest('/widget_registry?status=active&_sort=display_order&_order=asc'),
  getByCode: (code) => apiRequest(`/widget_registry?code=${code}`),
  create: (widget) => apiRequest('/widget_registry', { method: 'POST', body: JSON.stringify(widget) }),
  update: (id, widget) => apiRequest(`/widget_registry/${id}`, { method: 'PUT', body: JSON.stringify(widget) }),
};

// =============================================================================
// MONITOR API
// =============================================================================

/**
 * Monitor API - Live system monitoring
 */
export const monitorAPI = {
  getHealth: () => apiRequest('/monitor/health'),

  getActivity: (options = {}) => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.feature) params.append('feature', options.feature);
    if (options.since) params.append('since', options.since);
    const query = params.toString();
    return apiRequest(`/monitor/activity${query ? `?${query}` : ''}`);
  },

  getMetrics: (options = {}) => {
    const params = new URLSearchParams();
    if (options.hours) params.append('hours', options.hours);
    if (options.feature) params.append('feature', options.feature);
    const query = params.toString();
    return apiRequest(`/monitor/metrics${query ? `?${query}` : ''}`);
  },

  getAppViews: (includeDisabled = false) =>
    apiRequest(`/monitor/app_views${includeDisabled ? '?include_disabled=true' : ''}`),

  updateAppView: (code, data) => apiRequest(`/monitor/app_views/${code}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  createAppView: (data) => apiRequest('/monitor/app_views', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  getTechStackSnapshot: () => apiRequest('/monitor/tech-stack-snapshot'),

  downloadTechStackSnapshot: async (format = 'json') => {
    const response = await fetch(`${API_BASE_URL}/monitor/tech-stack-snapshot?format=${format}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to download tech stack snapshot');
    const blob = await response.blob();
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `tech-stack-${timestamp}.${format === 'md' ? 'md' : 'json'}`;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
};

// =============================================================================
// SIDEBAR ORCHESTRATOR API (Phase 1 - Feature Flag)
// =============================================================================

/**
 * Sidebar Orchestrator API - DB-driven sidebar resolution
 *
 * Feature Flag: Enable with localStorage.setItem('sidebar_orchestrator_enabled', 'true')
 *
 * Endpoints:
 * - resolve: Get resolved sidebar for current user context
 * - compare: Shadow mode - compare current vs resolver output
 * - validate: Validate override patch without applying
 * - overrides: Manage stored overrides
 */
export const sidebarAPI = {
  /**
   * Check if orchestrator feature flag is enabled
   */
  isEnabled: () => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('sidebar_orchestrator_enabled') === 'true';
  },

  /**
   * Enable/disable orchestrator feature flag
   */
  setEnabled: (enabled) => {
    if (typeof window === 'undefined') return;
    if (enabled) {
      localStorage.setItem('sidebar_orchestrator_enabled', 'true');
    } else {
      localStorage.removeItem('sidebar_orchestrator_enabled');
    }
  },

  /**
   * Resolve sidebar for current user context
   * @param {Object} context - Optional context override
   * @returns {Promise<Object>} Resolved sidebar groups
   */
  resolve: (context = {}) => uiApiRequest('/sidebar/resolve', {
    method: 'POST',
    body: JSON.stringify({
      profile_code: context.profileCode,
      mode: context.mode || 'elusive',
      roles: context.roles,
      override: context.override,
    }),
  }),

  /**
   * Compare current sidebar with resolver output (shadow mode)
   * @param {Object} options - Comparison options
   * @returns {Promise<Object>} Comparison result with diff
   */
  compare: (options = {}) => {
    const params = new URLSearchParams();
    if (options.profileCode) params.append('profile_code', options.profileCode);
    if (options.mode) params.append('mode', options.mode);
    const query = params.toString();
    return uiApiRequest(`/sidebar/compare${query ? `?${query}` : ''}`);
  },

  /**
   * Get default sidebar (no overrides applied)
   * @param {Object} options - Options
   * @returns {Promise<Object>} Default sidebar groups
   */
  getDefault: (options = {}) => {
    const params = new URLSearchParams();
    if (options.profileCode) params.append('profile_code', options.profileCode);
    if (options.mode) params.append('mode', options.mode);
    const query = params.toString();
    return uiApiRequest(`/sidebar/default${query ? `?${query}` : ''}`);
  },

  /**
   * Validate override patch without applying
   * @param {Object} override - Override patch to validate
   * @returns {Promise<Object>} Validation result
   */
  validate: (override) => uiApiRequest('/sidebar/validate', {
    method: 'POST',
    body: JSON.stringify({ override }),
  }),

  /**
   * List stored overrides
   * @param {Object} options - Filter options
   * @returns {Promise<Object>} List of overrides
   */
  getOverrides: (options = {}) => {
    const params = new URLSearchParams();
    if (options.scopeType) params.append('scope_type', options.scopeType);
    if (options.scopeKey) params.append('scope_key', options.scopeKey);
    if (options.includeDisabled) params.append('include_disabled', 'true');
    const query = params.toString();
    return uiApiRequest(`/sidebar/overrides${query ? `?${query}` : ''}`);
  },

  /**
   * Create new override
   * @param {Object} data - Override data
   * @returns {Promise<Object>} Created override ID
   */
  createOverride: (data) => uiApiRequest('/sidebar/overrides', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  /**
   * Update override
   * @param {number} id - Override ID
   * @param {Object} data - Update data
   * @returns {Promise<Object>} Update result
   */
  updateOverride: (id, data) => uiApiRequest(`/sidebar/overrides/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// =============================================================================
// FEATURE TRACKING API (App Radar)
// =============================================================================

/**
 * Feature Tracking API - App development radar
 */
export const featureTrackingAPI = {
  getAll: () => apiRequest('/feature_tracking'),
  getSummary: () => apiRequest('/feature_tracking/summary'),
  getByCategory: (categoryId) => apiRequest(`/feature_tracking/category/${categoryId}`),
  update: (id, data) => apiRequest(`/feature_tracking/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

/**
 * Feature Subtasks API - Subtask tracking
 */
export const featureSubtasksAPI = {
  getAll: () => apiRequest('/feature_subtasks'),
  getByFeature: (featureId) => apiRequest(`/feature_subtasks/feature/${featureId}`),
  create: (data) => apiRequest('/feature_subtasks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/feature_subtasks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/feature_subtasks/${id}`, {
    method: 'DELETE',
  }),
};

/**
 * Feature Health API - Operational status monitoring
 */
export const featureHealthAPI = {
  getAll: () => apiRequest('/feature_health'),
  getById: (id) => apiRequest(`/feature_health/${id}`),
  getSummary: () => apiRequest('/feature_health/summary'),
  update: (id, data) => apiRequest(`/feature_health/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  reportDefect: (id, data) => apiRequest(`/feature_health/${id}/report`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getHistory: (id) => apiRequest(`/feature_health/${id}/history`),
  markWorking: (id, userId, userName) => apiRequest(`/feature_health/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      health_status: 'working',
      last_checked_at: new Date().toISOString(),
      last_checked_by: userId,
      notes: `Marked as working by ${userName}`,
    }),
  }),
};

// =============================================================================
// ONBOARDING API
// =============================================================================

/**
 * Onboarding API - Fleet setup wizard
 */
export const onboardingAPI = {
  checkNeeded: () => apiRequest('/onboarding/check'),
  getCompleteness: () => apiRequest('/onboarding/completeness'),
  generate: (config) => apiRequest('/onboarding/generate', {
    method: 'POST',
    body: JSON.stringify(config),
  }),
  reset: () => apiRequest('/onboarding/reset', {
    method: 'POST',
  }),
};

// =============================================================================
// WELCOME API (First Login + What's New)
// =============================================================================

/**
 * Welcome API - First login greeting and what's new
 */
export const welcomeAPI = {
  /**
   * Check if welcome modal should be shown
   * Returns: { show, isFirstLogin, hasNewVersion, currentVersion }
   */
  check: () => apiRequest('/welcome/check'),

  /**
   * Get what's new items for a version
   * @param {string} version - Version to get items for (optional, defaults to current)
   */
  getWhatsNew: (version) => apiRequest(`/welcome/whats-new${version ? `?version=${version}` : ''}`),

  /**
   * Dismiss welcome modal (marks as seen)
   */
  dismiss: () => apiRequest('/welcome/dismiss', {
    method: 'POST',
  }),

  /**
   * Get current app version
   */
  getVersion: () => apiRequest('/welcome/version'),
};

// =============================================================================
// TOUR API (Interactive Feature Tour)
// =============================================================================

/**
 * Tour API - Feature tour system
 */
export const tourAPI = {
  /**
   * Get tour steps for a specific tour
   * @param {string} tourId - Tour ID (default: 'main')
   */
  getSteps: (tourId = 'main') => apiRequest(`/tour/steps/${tourId}`),

  /**
   * Get tour completion status for current user
   */
  getStatus: () => apiRequest('/tour/status'),

  /**
   * Mark tour as completed
   * @param {string} tourId - Tour ID (optional)
   */
  complete: (tourId) => apiRequest('/tour/complete', {
    method: 'POST',
    body: JSON.stringify({ tourId }),
  }),

  /**
   * Reset tour completion status (allow re-taking)
   */
  reset: () => apiRequest('/tour/reset', {
    method: 'POST',
  }),

  /**
   * Get list of available tours
   */
  getAvailable: () => apiRequest('/tour/available'),
};

// =============================================================================
// ACCORDION STATE API
// =============================================================================

/**
 * Accordion State API - UI state persistence
 */
export const accordionStateAPI = {
  getForView: async (viewCode) => {
    const response = await fetch(`${API_BASE_URL}/accordion-state/${viewCode}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  set: async (viewCode, accordionKey, isExpanded) => {
    const response = await fetch(`${API_BASE_URL}/accordion-state/${viewCode}/${accordionKey}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ is_expanded: isExpanded }),
    });
    return response.json();
  },

  reset: async () => {
    const response = await fetch(`${API_BASE_URL}/accordion-state/reset`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

// =============================================================================
// TENANT ARCHIVE API (Offboarded Customers)
// =============================================================================

/**
 * Tenant Archive API - For offboarded/archived customers
 */
export const tenantArchiveAPI = {
  getArchiveInfo: () => apiRequest('/tenant/archive-info'),
};
